import React, { useEffect, useMemo, useState } from 'react';
import { X, LogOut } from 'lucide-react';
import api from '../services/api';
import './SuperAdminDashboard.css';

function SuperAdminDashboard({ user, onLogout }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [labelsTarget, setLabelsTarget] = useState(null);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [labelsSaving, setLabelsSaving] = useState(false);
  const [labelsForm, setLabelsForm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toast, setToast] = useState(null);

  const loadCompanies = async () => {
    setLoading(true);
    setToast(null);
    try {
      const data = await api.getCompanies();
      setCompanies(data);
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleDeleteCompany = async (company) => {
    setToast(null);
    setToast(null);
    setDeletingId(company.id);
    try {
      await api.deleteCompany(company.id);
      setCompanies((prev) => prev.filter((item) => item.id !== company.id));
      setToast({ type: 'success', message: `Deleted ${company.name}.` });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (company) => {
    setToast(null);
    setToast(null);
    try {
      const updated = await api.updateCompanyStatus(company.id, !company.is_active);
      setCompanies((prev) =>
        prev.map((item) =>
          item.id === company.id
            ? { ...item, is_active: updated.is_active, disabled_at: updated.disabled_at }
            : item
        )
      );
      setToast({ type: 'success', message: `${company.name} ${updated.is_active ? 'enabled' : 'disabled'}.` });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleResetAdmin = async (company) => {
    setToast(null);
    setToast(null);
    try {
      await api.resetCompanyAdmin(company.id);
      setToast({ type: 'success', message: `Admin password reset for ${company.name}.` });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleUnlockAdmin = async (company) => {
    setToast(null);
    try {
      await api.unlockCompanyAdmin(company.id);
      setToast({ type: 'success', message: `Admin unlocked for ${company.name}.` });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleImpersonate = async (company) => {
    setToast(null);
    setToast(null);
    try {
      const data = await api.impersonateCompanyAdmin(company.id);
      const currentToken = api.getToken();
      const currentUser = localStorage.getItem('user');
      if (currentToken && currentUser) {
        localStorage.setItem('superadmin_token', currentToken);
        localStorage.setItem('superadmin_user', currentUser);
      }
      localStorage.setItem('impersonation_token', data.token);
      localStorage.setItem('impersonation_user', JSON.stringify(data.user));
      window.open('/impersonate', '_blank', 'noopener');
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const openLabelEditor = async (company) => {
    setToast(null);
    setLabelsTarget(company);
    setLabelsLoading(true);
    setLabelsForm(null);
    try {
      const data = await api.getCompanyLabels(company.id);
      const tabs = Array.isArray(data.dashboard_tabs)
        ? data.dashboard_tabs.map((tab) => ({
            id: tab.id,
            label: tab.label || tab.id,
            visible: tab.visible
          }))
        : [];
      const fields = Array.isArray(data.transaction_schema?.fields)
        ? data.transaction_schema.fields.map((field) => ({
            key: field.key,
            label: field.label || field.key,
            type: field.type
          }))
        : [];
      const lists = Array.isArray(data.custom_lists)
        ? data.custom_lists.map((list) => ({
            id: list.id,
            list_key: list.list_key,
            label: list.label || list.list_key
          }))
        : [];
      setLabelsForm({ tabs, fields, lists });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
      setLabelsTarget(null);
    } finally {
      setLabelsLoading(false);
    }
  };

  const closeLabelEditor = () => {
    setLabelsTarget(null);
    setLabelsForm(null);
    setLabelsLoading(false);
    setLabelsSaving(false);
  };

  const handleSaveLabels = async () => {
    if (!labelsTarget || !labelsForm) return;
    setLabelsSaving(true);
    setToast(null);
    try {
      await api.updateCompanyLabels(labelsTarget.id, {
        dashboard_tabs: labelsForm.tabs.map((tab) => ({ id: tab.id, label: tab.label })),
        transaction_fields: labelsForm.fields.map((field) => ({ key: field.key, label: field.label })),
        custom_lists: labelsForm.lists.map((list) => ({ id: list.id, label: list.label }))
      });
      setToast({ type: 'success', message: `Labels updated for ${labelsTarget.name}.` });
      closeLabelEditor();
    } catch (err) {
      setToast({ type: 'error', message: err.message });
      setLabelsSaving(false);
    }
  };

  const closeConfirm = () => {
    setConfirmTarget(null);
    setConfirmText('');
    setConfirmChecked(false);
  };

  const closeResetConfirm = () => {
    setResetTarget(null);
  };

  const closeUnlockConfirm = () => {
    setUnlockTarget(null);
  };

  const filteredCompanies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return companies.filter((company) => {
      const matchesTerm = !term ||
        company.name.toLowerCase().includes(term) ||
        company.slug.toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && company.is_active === 1) ||
        (statusFilter === 'disabled' && company.is_active !== 1);
      return matchesTerm && matchesStatus;
    });
  }, [companies, searchTerm, statusFilter]);

  const totalCompanies = companies.length;
  const activeCompanies = companies.filter((company) => company.is_active === 1).length;
  const disabledCompanies = totalCompanies - activeCompanies;


  return (
    <div className="super-admin-dashboard">
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button type="button" onClick={() => setToast(null)}>
            <X size={14} />
          </button>
        </div>
      )}
      <header className="super-admin-header">
        <div>
          <h1>Super Admin</h1>
          <p>Welcome, {user.username}</p>
        </div>
        <button type="button" className="logout-button" onClick={onLogout}>
          <LogOut className="btn-icon" size={16} />
          <span>Logout</span>
        </button>
      </header>

      <section className="super-admin-panel summary-grid">
        <div className="summary-card">
          <span>Total Companies</span>
          <strong>{totalCompanies}</strong>
        </div>
        <div className="summary-card">
          <span>Active</span>
          <strong>{activeCompanies}</strong>
        </div>
        <div className="summary-card">
          <span>Disabled</span>
          <strong>{disabledCompanies}</strong>
        </div>
        <div className="summary-card summary-action">
          <span>Audit Logs</span>
          <button
            type="button"
            className="secondary-button"
            onClick={() => window.open('/superadmin/audit', '_blank', 'noopener')}
          >
            View Audit Logs
          </button>
        </div>
      </section>

      <section className="super-admin-panel">
        <h2>Create Company</h2>
        <p className="helper-text">Launch the guided setup to configure settings before activation.</p>
        <button
          type="button"
          className="primary-button"
          onClick={() => window.open('/superadmin/setup', '_blank', 'noopener')}
        >
          Open Setup Wizard
        </button>
      </section>

      <section className="super-admin-panel">
        <div className="company-header-row">
          <h2>Companies</h2>
          <div className="company-filters">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name or slug"
            />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="status-message">Loading companies...</div>
        ) : filteredCompanies.length === 0 ? (
          <div className="status-message">No companies match your filters.</div>
        ) : (
          <div className="company-table">
            <div className="company-row header">
              <span>Name</span>
              <span>Slug</span>
              <span>Status</span>
              <span>Users</span>
              <span>Orders</span>
              <span>Transactions</span>
              <span>Last Login</span>
              <span>Actions</span>
            </div>
            {filteredCompanies.map((company) => (
              <div key={company.id} className="company-row">
                <span>{company.name}</span>
                <span>{company.slug}</span>
                <span>
                  <span className={`status-pill ${company.is_active ? 'active' : 'disabled'}`}>
                    {company.is_active ? 'Active' : 'Disabled'}
                  </span>
                </span>
                <span>{company.user_count}</span>
                <span>{company.order_count}</span>
                <span>{company.transaction_count}</span>
                <span>{company.last_login_at || '—'}</span>
                <span className="action-group">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => handleToggleStatus(company)}
                    disabled={deletingId === company.id}
                  >
                    {company.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setResetTarget(company)}
                    disabled={deletingId === company.id}
                  >
                    Reset Admin
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setUnlockTarget(company)}
                    disabled={deletingId === company.id}
                  >
                    Unlock Admin
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => handleImpersonate(company)}
                    disabled={deletingId === company.id || company.is_active !== 1}
                  >
                    Impersonate
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => openLabelEditor(company)}
                    disabled={deletingId === company.id}
                  >
                    Edit Labels
                  </button>
                  <button
                    type="button"
                    className="danger-button"
                    onClick={() => setConfirmTarget(company)}
                    disabled={deletingId === company.id}
                  >
                    {deletingId === company.id ? 'Deleting...' : 'Delete'}
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {confirmTarget && (
        <div className="modal-overlay">
          <div className="modal-card" role="dialog" aria-modal="true">
            <h3>Delete company?</h3>
            <p>
              You are about to delete <strong>{confirmTarget.name}</strong>. This removes all company data.
            </p>
            <label className="confirm-label">
              Type <strong>DELETE {confirmTarget.slug}</strong> to confirm.
              <input
                type="text"
                value={confirmText}
                onChange={(event) => setConfirmText(event.target.value)}
                placeholder={`DELETE ${confirmTarget.slug}`}
                autoFocus
              />
            </label>
            <label className="confirm-check">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(event) => setConfirmChecked(event.target.checked)}
              />
              I understand this action is irreversible.
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={closeConfirm}
                disabled={deletingId === confirmTarget.id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-button"
                onClick={() => {
                  handleDeleteCompany(confirmTarget);
                  closeConfirm();
                }}
                disabled={
                  deletingId === confirmTarget.id ||
                  !confirmChecked ||
                  confirmText.trim() !== `DELETE ${confirmTarget.slug}`
                }
              >
                {deletingId === confirmTarget.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="modal-overlay">
          <div className="modal-card" role="dialog" aria-modal="true">
            <h3>Reset admin password?</h3>
            <p>
              This will reset the admin password for <strong>{resetTarget.name}</strong> to the
              configured default.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={closeResetConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-button"
                onClick={() => {
                  handleResetAdmin(resetTarget);
                  closeResetConfirm();
                }}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockTarget && (
        <div className="modal-overlay">
          <div className="modal-card" role="dialog" aria-modal="true">
            <h3>Unlock admin?</h3>
            <p>
              This will clear the admin lock for <strong>{unlockTarget.name}</strong>.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={closeUnlockConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => {
                  handleUnlockAdmin(unlockTarget);
                  closeUnlockConfirm();
                }}
              >
                Unlock Admin
              </button>
            </div>
          </div>
        </div>
      )}

      {labelsTarget && (
        <div className="modal-overlay">
          <div className="modal-card modal-large" role="dialog" aria-modal="true">
            <h3>Edit labels for {labelsTarget.name}</h3>
            {labelsLoading ? (
              <div className="status-message">Loading labels...</div>
            ) : labelsForm ? (
              <div className="label-editor">
                <div className="label-editor-section">
                  <h4>Navigation Tabs</h4>
                  <div className="label-grid">
                    {labelsForm.tabs.map((tab) => (
                      <label key={tab.id} className="label-row">
                        <span>{tab.id}</span>
                        <input
                          type="text"
                          value={tab.label}
                          onChange={(event) =>
                            setLabelsForm((prev) => ({
                              ...prev,
                              tabs: prev.tabs.map((item) =>
                                item.id === tab.id ? { ...item, label: event.target.value } : item
                              )
                            }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="label-editor-section">
                  <h4>Transaction Fields</h4>
                  <div className="label-grid">
                    {labelsForm.fields.map((field) => (
                      <label key={field.key} className="label-row">
                        <span>{field.key}</span>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(event) =>
                            setLabelsForm((prev) => ({
                              ...prev,
                              fields: prev.fields.map((item) =>
                                item.key === field.key ? { ...item, label: event.target.value } : item
                              )
                            }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="label-editor-section">
                  <h4>Settings Lists</h4>
                  {labelsForm.lists.length === 0 ? (
                    <div className="status-message">No lists yet.</div>
                  ) : (
                    <div className="label-grid">
                      {labelsForm.lists.map((list) => (
                        <label key={list.id} className="label-row">
                          <span>{list.list_key}</span>
                          <input
                            type="text"
                            value={list.label}
                            onChange={(event) =>
                              setLabelsForm((prev) => ({
                                ...prev,
                                lists: prev.lists.map((item) =>
                                  item.id === list.id ? { ...item, label: event.target.value } : item
                                )
                              }))
                            }
                          />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="status-message error">Unable to load labels.</div>
            )}
            <div className="modal-actions">
              <button type="button" className="secondary-button" onClick={closeLabelEditor}>
                Cancel
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={handleSaveLabels}
                disabled={labelsLoading || labelsSaving || !labelsForm}
              >
                {labelsSaving ? 'Saving...' : 'Save Labels'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
